import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { Event as ResioEvent } from '@/types/residents/event';

interface DesktopEventsTileProps {
  events: ResioEvent[];
  onEventPress: (event: ResioEvent) => void;
  onViewAllPress: () => void;
}

/**
 * DesktopEventsTile - Upcoming events preview tile for desktop home dashboard
 */
export const DesktopEventsTile: React.FC<DesktopEventsTileProps> = ({
  events,
  onEventPress,
  onViewAllPress,
}) => {
  const hasEvents = events.length > 0;
  const displayEvents = events.slice(0, 3); // Show max 3 events

  const formatEventDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatEventTime = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <View style={styles.tile}>
      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <RESIDENTS_SVG_COMPONENTS.STAR width={24} height={24} color={PRIMITIVE_COLORS.resioBlue} />
        </View>
        <Text style={styles.title}>Upcoming Events</Text>
        {hasEvents && (
          <TouchableOpacity onPress={onViewAllPress}>
            <Text style={styles.viewAllText}>View all</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.content}>
        {!hasEvents ? (
          <View style={styles.emptyState}>
            <RESIDENTS_SVG_COMPONENTS.NO_EVENTS_ICON width={48} height={48} color={SEMANTIC_COLORS.text.tertiary} />
            <Text style={styles.emptyText}>No upcoming events</Text>
            <Text style={styles.emptySubtext}>Check back later for community events</Text>
          </View>
        ) : (
          <ScrollView style={styles.eventsList} showsVerticalScrollIndicator={false}>
            {displayEvents.map((event, index) => (
              <TouchableOpacity
                key={event.id}
                style={[styles.eventItem, index !== displayEvents.length - 1 && styles.eventItemBorder]}
                onPress={() => onEventPress(event)}
                activeOpacity={0.7}
              >
                <View style={styles.eventDateBadge}>
                  <Text style={styles.eventDateMonth}>{formatEventDate(event.startTime).split(' ')[0]}</Text>
                  <Text style={styles.eventDateDay}>{formatEventDate(event.startTime).split(' ')[1]}</Text>
                </View>
                <View style={styles.eventInfo}>
                  <Text style={styles.eventTitle} numberOfLines={1}>{event.name}</Text>
                  <Text style={styles.eventTime}>{formatEventTime(event.startTime)}</Text>
                </View>
                <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT width={20} height={20} color={SEMANTIC_COLORS.text.tertiary} />
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  tile: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 20,
    minHeight: 200,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: PRIMITIVE_COLORS.white10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    flex: 1,
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlueLight,
  },
  content: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    marginTop: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: SEMANTIC_COLORS.text.tertiary,
  },
  eventsList: {
    flex: 1,
  },
  eventItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  eventItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: PRIMITIVE_COLORS.gray850,
  },
  eventDateBadge: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: PRIMITIVE_COLORS.resioBlueBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eventDateMonth: {
    fontSize: 10,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.resioBlueLight,
    textTransform: 'uppercase',
  },
  eventDateDay: {
    fontSize: 16,
    fontWeight: '700',
    color: SEMANTIC_COLORS.text.primary,
  },
  eventInfo: {
    flex: 1,
  },
  eventTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    marginBottom: 2,
  },
  eventTime: {
    fontSize: 13,
    color: SEMANTIC_COLORS.text.tertiary,
  },
});

export default DesktopEventsTile;
